/**
 * Finance & Investment Glossary
 * Comprehensive definitions for value investing concepts
 */

const glossary = [
  // Valuation Metrics
  {
    term: 'P/E Ratio',
    aka: ['Price-to-Earnings', 'PE', 'PER'],
    category: 'Valuation',
    definition: 'The ratio of a company\'s stock price to its earnings per share (EPS). It shows how much investors are willing to pay for each dollar of earnings.',
    formula: 'P/E = Stock Price / Earnings Per Share',
    interpretation: 'Lower P/E may indicate undervaluation. A P/E of 15 is often considered "fair value" for mature companies. High-growth companies typically have higher P/Es.',
    example: 'If a stock trades at $100 and EPS is $5, P/E = 20. This means investors pay $20 for every $1 of earnings.'
  },
  {
    term: 'Trailing P/E',
    aka: ['TTM P/E', 'Historical P/E'],
    category: 'Valuation',
    definition: 'P/E ratio calculated using actual earnings from the past 12 months (Trailing Twelve Months). This is backward-looking and based on real data.',
    formula: 'Trailing P/E = Current Price / EPS (last 12 months)',
    interpretation: 'More reliable than forward P/E since it uses actual reported earnings, but may not reflect future changes in profitability.',
    example: 'A company earned $4.00 per share over the last year. At $80/share, trailing P/E = 20.'
  },
  {
    term: 'Forward P/E',
    aka: ['Projected P/E', 'Estimated P/E'],
    category: 'Valuation',
    definition: 'P/E ratio calculated using estimated future earnings (usually next 12 months). This is forward-looking based on analyst projections.',
    formula: 'Forward P/E = Current Price / Estimated Future EPS',
    interpretation: 'Useful for growth companies. Compare to trailing P/E: if forward P/E is lower, analysts expect earnings growth.',
    example: 'Analysts estimate next year\'s EPS at $5.00. At $80/share, forward P/E = 16 (vs trailing P/E of 20 suggests expected growth).'
  },
  {
    term: 'P/B Ratio',
    aka: ['Price-to-Book', 'PBR'],
    category: 'Valuation',
    definition: 'The ratio of a company\'s market value to its book value (net assets). Shows how much investors pay for each dollar of net assets.',
    formula: 'P/B = Stock Price / Book Value Per Share',
    interpretation: 'P/B < 1 may indicate undervaluation or financial trouble. P/B < 1.5 is often considered attractive for value investors. Asset-heavy industries typically have lower P/B.',
    example: 'Book value per share is $50, stock price is $75. P/B = 1.5, meaning you pay 1.5x the company\'s net asset value.'
  },
  {
    term: 'Graham Number',
    aka: ['Benjamin Graham Number', 'Graham Value'],
    category: 'Valuation',
    definition: 'A figure that measures a stock\'s fundamental value by combining earnings and book value. Named after Benjamin Graham, the father of value investing.',
    formula: 'Graham Number = √(22.5 × EPS × Book Value Per Share)',
    interpretation: 'If stock price is below the Graham Number, it may be undervalued. The 22.5 comes from Graham\'s criteria: P/E < 15 and P/B < 1.5 (15 × 1.5 = 22.5).',
    example: 'EPS = $5, BVPS = $40. Graham Number = √(22.5 × 5 × 40) = √4500 = $67.08. If stock trades at $50, it\'s potentially undervalued.'
  },
  {
    term: 'Intrinsic Value',
    aka: ['Fair Value', 'True Value'],
    category: 'Valuation',
    definition: 'The "true" worth of a company based on fundamental analysis, independent of its current market price. Different methods (DCF, Graham Number, etc.) estimate this differently.',
    formula: 'Varies by method (DCF, asset-based, earnings-based)',
    interpretation: 'Buy when market price < intrinsic value (undervalued). Sell when market price > intrinsic value (overvalued). Always use a margin of safety.',
    example: 'If you calculate intrinsic value at $100 and the stock trades at $70, you have a potential 30% margin of safety.'
  },
  {
    term: 'Margin of Safety',
    aka: ['MoS', 'Safety Margin'],
    category: 'Valuation',
    definition: 'The difference between a stock\'s intrinsic value and its market price, expressed as a percentage. A core concept in value investing to protect against errors.',
    formula: 'MoS = (Intrinsic Value - Market Price) / Intrinsic Value × 100%',
    interpretation: 'Higher MoS = lower risk. Benjamin Graham recommended at least 33% margin of safety. Positive MoS means potentially undervalued.',
    example: 'Intrinsic value = $100, market price = $70. MoS = (100-70)/100 = 30%. The stock is 30% below estimated fair value.'
  },
  {
    term: 'DCF',
    aka: ['Discounted Cash Flow', 'DCF Analysis', 'DCF Model'],
    category: 'Valuation',
    definition: 'A valuation method that estimates the present value of a company based on projections of future free cash flows, discounted back to today\'s dollars.',
    formula: 'DCF Value = Σ(FCF_t / (1+r)^t) + Terminal Value / (1+r)^n',
    interpretation: 'Highly sensitive to growth rate and discount rate assumptions. Best for mature companies with predictable cash flows. Requires estimating future performance.',
    example: 'If a company generates $10M FCF yearly, growing at 5%, with 10% discount rate, DCF estimates what those future cash flows are worth today.'
  },
  {
    term: 'Terminal Value',
    aka: ['Continuing Value', 'Horizon Value'],
    category: 'Valuation',
    definition: 'In DCF analysis, the estimated value of a business beyond the explicit forecast period, assuming stable growth in perpetuity.',
    formula: 'Terminal Value = Final Year FCF × (1 + g) / (r - g)',
    interpretation: 'Often accounts for 60-80% of total DCF value. Small changes in terminal growth rate significantly impact valuation. Use conservative estimates.',
    example: 'Year 5 FCF = $15M, terminal growth = 2%, discount rate = 10%. TV = 15 × 1.02 / (0.10 - 0.02) = $191.25M'
  },
  {
    term: 'Discount Rate',
    aka: ['Required Return', 'WACC'],
    category: 'Valuation',
    definition: 'The rate used to discount future cash flows to present value in DCF analysis. Often approximated by WACC (Weighted Average Cost of Capital) or required rate of return.',
    formula: 'WACC = (E/V × Re) + (D/V × Rd × (1-T))',
    interpretation: 'Higher discount rate = lower present value. Use higher rates for riskier companies. Typical range: 8-12% for established companies, higher for startups.',
    example: 'A 10% discount rate means $110 received next year is worth $100 today. Higher risk companies warrant higher rates.'
  },

  // Profitability Metrics
  {
    term: 'EPS',
    aka: ['Earnings Per Share'],
    category: 'Profitability',
    definition: 'The portion of a company\'s profit allocated to each outstanding share of common stock. A key indicator of profitability.',
    formula: 'EPS = (Net Income - Preferred Dividends) / Shares Outstanding',
    interpretation: 'Higher EPS indicates more profit per share. Compare YoY growth and to industry peers. Watch for share buybacks artificially boosting EPS.',
    example: 'Net income = $100M, shares outstanding = 20M. EPS = $5.00 per share.'
  },
  {
    term: 'ROE',
    aka: ['Return on Equity'],
    category: 'Profitability',
    definition: 'Measures how efficiently a company uses shareholder equity to generate profits. Shows the return generated on shareholders\' investment.',
    formula: 'ROE = Net Income / Shareholders\' Equity × 100%',
    interpretation: 'Higher ROE = more efficient use of equity. 15%+ is generally good. Very high ROE may indicate high debt. Compare within same industry.',
    example: 'Net income = $50M, equity = $250M. ROE = 20%. For every $1 of equity, the company generates $0.20 in profit.'
  },
  {
    term: 'ROA',
    aka: ['Return on Assets'],
    category: 'Profitability',
    definition: 'Measures how efficiently a company uses its total assets to generate profits. Shows management effectiveness in using company resources.',
    formula: 'ROA = Net Income / Total Assets × 100%',
    interpretation: 'Higher ROA = better asset utilization. 5%+ is generally acceptable. Asset-heavy industries (utilities) have lower ROA than asset-light (software).',
    example: 'Net income = $50M, total assets = $500M. ROA = 10%. Each dollar of assets generates 10 cents of profit.'
  },
  {
    term: 'ROIC',
    aka: ['Return on Invested Capital'],
    category: 'Profitability',
    definition: 'Measures how well a company generates returns from the capital invested in its business operations. A key metric for assessing competitive advantage.',
    formula: 'ROIC = NOPAT / Invested Capital',
    interpretation: 'ROIC > WACC creates shareholder value. Consistently high ROIC (15%+) may indicate competitive moat. Warren Buffett\'s favorite metric.',
    example: 'NOPAT = $80M, invested capital = $400M. ROIC = 20%. If WACC is 10%, the company creates value.'
  },
  {
    term: 'Profit Margin',
    aka: ['Net Margin', 'Net Profit Margin'],
    category: 'Profitability',
    definition: 'The percentage of revenue that becomes profit after all expenses. Shows how much of each sales dollar turns into profit.',
    formula: 'Net Margin = Net Income / Revenue × 100%',
    interpretation: 'Higher margins = more profitable. Varies greatly by industry. Software: 20-30%, Retail: 2-5%, Banking: 20-30%.',
    example: 'Revenue = $1B, net income = $100M. Net margin = 10%. The company keeps 10 cents profit from every dollar of sales.'
  },
  {
    term: 'Operating Margin',
    aka: ['Operating Profit Margin', 'EBIT Margin'],
    category: 'Profitability',
    definition: 'The percentage of revenue remaining after operating expenses. Measures core business profitability before interest and taxes.',
    formula: 'Operating Margin = Operating Income / Revenue × 100%',
    interpretation: 'Shows operational efficiency independent of financing decisions. Compare to industry average. Expanding margins indicate improving efficiency.',
    example: 'Revenue = $500M, operating income = $75M. Operating margin = 15%.'
  },

  // Dividend Metrics
  {
    term: 'Dividend Yield',
    aka: ['Yield', 'Div Yield'],
    category: 'Dividends',
    definition: 'The annual dividend payment divided by the stock price, expressed as a percentage. Shows the cash return on investment from dividends alone.',
    formula: 'Dividend Yield = Annual Dividend Per Share / Stock Price × 100%',
    interpretation: 'Higher yield = more income. Very high yields (8%+) may signal dividend cut risk. 2-4% is typical for stable dividend payers.',
    example: 'Annual dividend = $2, stock price = $50. Yield = 4%. You receive $4 annually for every $100 invested.'
  },
  {
    term: 'Dividend Payout Ratio',
    aka: ['Payout Ratio'],
    category: 'Dividends',
    definition: 'The percentage of earnings paid out as dividends. Shows how much profit is returned to shareholders vs. retained for growth.',
    formula: 'Payout Ratio = Dividends Per Share / EPS × 100%',
    interpretation: 'Lower ratio = more room for dividend growth. 40-60% is healthy for most companies. >100% is unsustainable long-term.',
    example: 'EPS = $5, DPS = $2. Payout ratio = 40%. The company pays out 40% of earnings as dividends.'
  },
  {
    term: 'Ex-Dividend Date',
    aka: ['Ex-Date'],
    category: 'Dividends',
    definition: 'The date on which a stock begins trading without the right to receive the upcoming dividend. You must own shares before this date to receive the dividend.',
    formula: 'N/A',
    interpretation: 'Buy before ex-date to receive dividend. Stock price typically drops by dividend amount on ex-date. Important for dividend capture strategies.',
    example: 'Ex-date is March 15. Buy on March 14 = receive dividend. Buy on March 15 = no dividend.'
  },
  {
    term: 'Dividend Growth Rate',
    aka: ['DGR'],
    category: 'Dividends',
    definition: 'The annualized percentage rate at which a company\'s dividend has grown over time. Key metric for dividend growth investors.',
    formula: 'DGR = (Current Dividend / Past Dividend)^(1/years) - 1',
    interpretation: '7-10% growth doubles dividend in 7-10 years. Consistent growth indicates financial health. Look for 5+ year track records.',
    example: 'Dividend grew from $1.00 to $1.61 over 5 years. DGR = (1.61/1.00)^(1/5) - 1 = 10% annually.'
  },

  // Financial Health
  {
    term: 'Debt-to-Equity',
    aka: ['D/E Ratio', 'Leverage Ratio'],
    category: 'Financial Health',
    definition: 'The ratio of a company\'s total debt to shareholders\' equity. Measures financial leverage and how much the company relies on debt financing.',
    formula: 'D/E = Total Debt / Shareholders\' Equity',
    interpretation: 'Lower D/E = less financial risk. D/E < 1 is generally conservative. Varies by industry - utilities and REITs have higher D/E.',
    example: 'Total debt = $200M, equity = $400M. D/E = 0.5. For every $1 of equity, the company has $0.50 in debt.'
  },
  {
    term: 'Current Ratio',
    aka: ['Working Capital Ratio'],
    category: 'Financial Health',
    definition: 'The ratio of current assets to current liabilities. Measures a company\'s ability to pay short-term obligations.',
    formula: 'Current Ratio = Current Assets / Current Liabilities',
    interpretation: 'Ratio > 1 means company can cover short-term debts. 1.5-2.0 is healthy. Too high may indicate inefficient asset use.',
    example: 'Current assets = $150M, current liabilities = $100M. Current ratio = 1.5.'
  },
  {
    term: 'Quick Ratio',
    aka: ['Acid-Test Ratio'],
    category: 'Financial Health',
    definition: 'Similar to current ratio but excludes inventory. A more conservative measure of short-term liquidity.',
    formula: 'Quick Ratio = (Current Assets - Inventory) / Current Liabilities',
    interpretation: 'Ratio > 1 indicates good short-term liquidity. More relevant for companies with slow-moving inventory.',
    example: 'Current assets = $150M, inventory = $50M, current liabilities = $100M. Quick ratio = 1.0.'
  },
  {
    term: 'Free Cash Flow',
    aka: ['FCF'],
    category: 'Financial Health',
    definition: 'Cash generated by operations minus capital expenditures. The actual cash available for dividends, buybacks, debt repayment, or acquisitions.',
    formula: 'FCF = Operating Cash Flow - Capital Expenditures',
    interpretation: 'Positive FCF is essential for financial health. More reliable than earnings (harder to manipulate). Used in DCF valuation.',
    example: 'Operating cash flow = $100M, CapEx = $30M. FCF = $70M available for shareholders.'
  },
  {
    term: 'Book Value',
    aka: ['Net Asset Value', 'Shareholders\' Equity'],
    category: 'Financial Health',
    definition: 'The net value of a company\'s assets minus liabilities. Represents what shareholders would theoretically receive if company liquidated.',
    formula: 'Book Value = Total Assets - Total Liabilities',
    interpretation: 'Floor value for company worth. Compare to market cap. Companies trading below book value may be undervalued or in distress.',
    example: 'Assets = $500M, liabilities = $300M. Book value = $200M. With 10M shares, BVPS = $20.'
  },

  // Market Terms
  {
    term: 'Market Cap',
    aka: ['Market Capitalization'],
    category: 'Market',
    definition: 'The total market value of a company\'s outstanding shares. Used to classify companies by size (large-cap, mid-cap, small-cap).',
    formula: 'Market Cap = Stock Price × Shares Outstanding',
    interpretation: 'Large-cap: >$10B (stable), Mid-cap: $2-10B (growth potential), Small-cap: <$2B (higher risk/reward).',
    example: 'Stock price = $100, shares = 50M. Market cap = $5B (mid-cap).'
  },
  {
    term: 'Enterprise Value',
    aka: ['EV', 'Firm Value'],
    category: 'Market',
    definition: 'The total value of a company including equity and debt, minus cash. Represents the theoretical takeover price.',
    formula: 'EV = Market Cap + Total Debt - Cash',
    interpretation: 'Better than market cap for comparing companies with different capital structures. Used in EV/EBITDA ratio.',
    example: 'Market cap = $500M, debt = $100M, cash = $50M. EV = $550M.'
  },
  {
    term: 'EV/EBITDA',
    aka: ['Enterprise Multiple'],
    category: 'Valuation',
    definition: 'Enterprise value divided by EBITDA (Earnings Before Interest, Taxes, Depreciation, Amortization). A capital structure-neutral valuation metric.',
    formula: 'EV/EBITDA = Enterprise Value / EBITDA',
    interpretation: 'Lower = potentially undervalued. Useful for comparing companies with different debt levels. Typical range: 6-12x.',
    example: 'EV = $550M, EBITDA = $50M. EV/EBITDA = 11x.'
  },
  {
    term: 'Beta',
    aka: ['Market Beta'],
    category: 'Market',
    definition: 'A measure of a stock\'s volatility relative to the overall market. Indicates how much a stock moves compared to market movements.',
    formula: 'Beta = Covariance(Stock, Market) / Variance(Market)',
    interpretation: 'Beta = 1: moves with market. Beta > 1: more volatile. Beta < 1: less volatile. Negative beta: moves opposite to market.',
    example: 'Beta = 1.5 means if market rises 10%, the stock typically rises 15% (and vice versa for declines).'
  },
  {
    term: 'Alpha',
    aka: ['Jensen\'s Alpha'],
    category: 'Market',
    definition: 'The excess return of an investment relative to its expected return based on its beta. Measures manager or strategy skill.',
    formula: 'Alpha = Actual Return - [Risk-Free Rate + Beta × (Market Return - Risk-Free Rate)]',
    interpretation: 'Positive alpha = outperformance. Negative alpha = underperformance. Difficult to consistently generate positive alpha.',
    example: 'If expected return was 10% based on beta, but actual return was 12%, alpha = 2%.'
  },

  // Technical/Trading
  {
    term: '52-Week High/Low',
    aka: ['52W High', '52W Low'],
    category: 'Technical',
    definition: 'The highest and lowest prices at which a stock has traded during the past year. Used as reference points for price analysis.',
    formula: 'N/A',
    interpretation: 'Near 52W high: momentum but potentially overextended. Near 52W low: potential value or continued decline. Context matters.',
    example: 'Stock trades at $80, 52W high = $100, 52W low = $60. Currently 20% below high, 33% above low.'
  },
  {
    term: 'Volume',
    aka: ['Trading Volume'],
    category: 'Technical',
    definition: 'The number of shares traded during a given period. Indicates liquidity and investor interest.',
    formula: 'N/A',
    interpretation: 'High volume on price moves = conviction. Low volume = weak conviction. Average daily volume helps assess liquidity.',
    example: 'Stock has 10M average daily volume. A day with 50M volume suggests significant news or interest.'
  },
  {
    term: 'Volatility',
    aka: ['Price Volatility', 'Standard Deviation'],
    category: 'Technical',
    definition: 'A statistical measure of the dispersion of returns. Higher volatility means larger price swings and higher risk.',
    formula: 'Volatility = Standard Deviation of Returns',
    interpretation: 'Higher volatility = higher risk and potential reward. Used in options pricing. Long-term investors can use volatility to buy low.',
    example: '20% annual volatility means returns typically range from -20% to +20% in a normal year.'
  },

  // Investment Concepts
  {
    term: 'Value Investing',
    aka: ['Value Strategy'],
    category: 'Strategy',
    definition: 'An investment strategy focused on buying stocks that trade below their intrinsic value. Pioneered by Benjamin Graham and Warren Buffett.',
    formula: 'N/A',
    interpretation: 'Focus on fundamentals over market sentiment. Requires patience. Look for margin of safety. Buy low, hold long-term.',
    example: 'Buying a stock trading at $70 when calculated intrinsic value is $100 (30% margin of safety).'
  },
  {
    term: 'Moat',
    aka: ['Economic Moat', 'Competitive Advantage'],
    category: 'Strategy',
    definition: 'A sustainable competitive advantage that protects a company\'s profits from competitors. Term popularized by Warren Buffett.',
    formula: 'N/A',
    interpretation: 'Wide moat = durable advantage (brands, patents, network effects, cost advantages). Companies with moats can maintain high ROIC.',
    example: 'Coca-Cola\'s brand recognition, Apple\'s ecosystem, and Amazon\'s scale are examples of economic moats.'
  },
  {
    term: 'Cost Basis',
    aka: ['Tax Basis', 'Purchase Price'],
    category: 'Portfolio',
    definition: 'The original price paid for an investment, used to calculate capital gains or losses for tax purposes.',
    formula: 'Cost Basis = Purchase Price + Commissions + Fees',
    interpretation: 'Selling above cost basis = capital gain (taxable). Selling below = capital loss (tax deductible). Track for tax planning.',
    example: 'Bought 100 shares at $50 + $10 commission. Cost basis = $5,010 total or $50.10 per share.'
  },
  {
    term: 'Unrealized Gain/Loss',
    aka: ['Paper Gain', 'Paper Loss'],
    category: 'Portfolio',
    definition: 'The profit or loss on an investment that you still hold. It becomes "realized" only when you sell.',
    formula: 'Unrealized Gain/Loss = Current Value - Cost Basis',
    interpretation: 'Not taxed until realized. Can turn into realized gain/loss when sold. Don\'t let unrealized gains dictate selling decisions.',
    example: 'Bought at $50, now worth $75. Unrealized gain = $25/share (50% gain). No tax until sold.'
  },
  {
    term: 'Dollar-Cost Averaging',
    aka: ['DCA'],
    category: 'Strategy',
    definition: 'Investing a fixed amount at regular intervals regardless of price. Reduces impact of volatility and removes emotional timing decisions.',
    formula: 'Average Cost = Total Invested / Total Shares Acquired',
    interpretation: 'Reduces risk of buying at peak. Works best in volatile, eventually rising markets. Good for long-term systematic investing.',
    example: 'Invest $500 monthly. Buy more shares when prices are low, fewer when high, averaging out the cost.'
  },
  {
    term: 'Compound Interest',
    aka: ['Compounding', 'Compound Growth'],
    category: 'General',
    definition: 'Interest calculated on both the initial principal and accumulated interest from previous periods. The key to long-term wealth building.',
    formula: 'Future Value = Present Value × (1 + r)^n',
    interpretation: 'Time is your friend. 10% return doubles money in ~7 years (Rule of 72). Reinvesting dividends accelerates compounding.',
    example: '$10,000 at 8% annual return: Year 1 = $10,800, Year 10 = $21,589, Year 30 = $100,627.'
  },
  {
    term: 'Diversification',
    aka: ['Portfolio Diversification'],
    category: 'Strategy',
    definition: 'Spreading investments across different assets, sectors, or geographies to reduce risk. "Don\'t put all eggs in one basket."',
    formula: 'N/A',
    interpretation: 'Reduces unsystematic risk. 20-30 stocks can achieve most diversification benefits. Over-diversification can limit returns.',
    example: 'Instead of 100% in tech stocks, hold 40% tech, 20% healthcare, 20% financials, 20% consumer staples.'
  },
  {
    term: 'Portfolio Allocation',
    aka: ['Asset Allocation'],
    category: 'Strategy',
    definition: 'How investments are distributed across asset classes (stocks, bonds, cash, real estate). Primary driver of portfolio risk and return.',
    formula: 'N/A',
    interpretation: 'Stocks = growth + volatility. Bonds = stability + income. Adjust allocation based on goals, timeline, and risk tolerance.',
    example: 'Age 30: 90% stocks, 10% bonds. Age 60: 60% stocks, 40% bonds. More conservative as retirement nears.'
  }
];

// Helper function to search glossary
export function searchGlossary(query) {
  if (!query || query.trim() === '') return glossary;

  const lowerQuery = query.toLowerCase().trim();

  return glossary.filter(item => {
    // Search in term
    if (item.term.toLowerCase().includes(lowerQuery)) return true;

    // Search in aliases
    if (item.aka?.some(alias => alias.toLowerCase().includes(lowerQuery))) return true;

    // Search in definition
    if (item.definition.toLowerCase().includes(lowerQuery)) return true;

    // Search in category
    if (item.category.toLowerCase().includes(lowerQuery)) return true;

    return false;
  });
}

// Get all categories
export function getCategories() {
  const categories = new Set(glossary.map(item => item.category));
  return Array.from(categories).sort();
}

// Filter by category
export function filterByCategory(category) {
  if (!category || category === 'All') return glossary;
  return glossary.filter(item => item.category === category);
}

// Get a specific term
export function getTerm(termName) {
  return glossary.find(item =>
    item.term.toLowerCase() === termName.toLowerCase() ||
    item.aka?.some(alias => alias.toLowerCase() === termName.toLowerCase())
  );
}

export default glossary;
